<template>
  <div>
    <div v-if="isAtInitialization()">
      <email-change-initialisation
        :accountId="accountId"
        :initialEmail="initialEmail"
        :loading-initial-email="loadingInitialEmail"
        @initiateEmailChange="onInitiateEmailChange"
      ></email-change-initialisation>
    </div>

    <div v-if="isAtCodeInput()">
      <email-change-code-input
        :accountId="accountId"
        @confirmEmailChange="onConfirmEmailChange"
      ></email-change-code-input>
    </div>

    <div v-if="isAtConfirmation()">
      <email-change-confirmation></email-change-confirmation>
    </div>
  </div>
</template>

<script setup lang="ts">
import EmailChangeInitialisation from '@/components/account-management/emailchange/EmailChangeInitialization.vue'
import EmailChangeCodeInput from '@/components/account-management/emailchange/EmailChangeCodeInput.vue'
import EmailChangeConfirmation from '@/components/account-management/emailchange/EmailChangeConfirmation.vue'
import { navigate } from '@/utils/NavigationUtils'
import { getPartnerProxyId } from '@/utils/KeycloakUtils'
import { fetchAccountsByPPID } from '@/stores/accountManagementService'
import { ResponseError } from 'account-api'
import { onBeforeMount, onBeforeUnmount, onMounted, ref } from 'vue'
import { usePoliciesStore } from '@/stores/policiesStore'

enum EmailChangeState {
  INITIALIZATION,
  CODE_INPUT,
  CONFIRMATION
}

const emailChangeState = ref(EmailChangeState.INITIALIZATION)
const policiesStore = usePoliciesStore()

const partnerProxyId = ref<string | null>(null)
const initialEmail = ref('')
const accountId = ref('')
const loadingInitialEmail = ref(false)

onBeforeMount(() => {
  partnerProxyId.value = getPartnerProxyId()
})

onBeforeUnmount(() => {
  policiesStore.resetProcessReturnContext()
})

onMounted(async () => {
  if (!partnerProxyId.value) {
    throw new Error('No partner proxy id available!')
  }
  loadingInitialEmail.value = true
  try {
    const accounts = await fetchAccountsByPPID(partnerProxyId.value)
    if (accounts && accounts.length > 0) {
      initialEmail.value = accounts[0]?.email || ''
      accountId.value = accounts[0]?.accountId || ''
    } else {
      navigate(globalThis.ENV_CONFIG?.REDIRECTS.TECHNICAL_ERROR_PAGE)
    }
  } catch (error) {
    if (error instanceof ResponseError) {
      if (error.response.status === 401) {
        navigate('/')
      } else {
        navigate(globalThis.ENV_CONFIG?.REDIRECTS.TECHNICAL_ERROR_PAGE)
      }
    } else {
      navigate(globalThis.ENV_CONFIG?.REDIRECTS.TECHNICAL_ERROR_PAGE)
    }
  } finally {
    loadingInitialEmail.value = false
  }
})

const isAtInitialization = (): boolean => {
  return emailChangeState.value === EmailChangeState.INITIALIZATION
}

const isAtCodeInput = (): boolean => {
  return emailChangeState.value === EmailChangeState.CODE_INPUT
}

const isAtConfirmation = (): boolean => {
  return emailChangeState.value === EmailChangeState.CONFIRMATION
}

const onInitiateEmailChange = (): void => {
  emailChangeState.value = EmailChangeState.CODE_INPUT
}

const onConfirmEmailChange = (): void => {
  emailChangeState.value = EmailChangeState.CONFIRMATION
}
</script>
<style scoped lang="scss">
//Do not remove this comment. Reason is that with empty style section the various scss-styles which are imported
// in vue.config.js are not used (see https://github.com/vuejs/vue-cli/issues/5188)
</style>
